import React, { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import {
  Megaphone, ExternalLink, RefreshCw, Users2, LogIn, ShieldCheck, Globe2,
} from 'lucide-react';
import BetaBadge from '../components/BetaBadge';

/**
 * Meta Ads Tracker
 *
 * Embeds the self-contained Derwent Meta Tracker (vanilla HTML/CSS/JS shipped
 * at `/meta-tracker.html`) inside a sandboxed iframe. The inner tool's
 * `load()` / `save()` read the JWT + backend URL out of the iframe URL hash
 * and persist to a shared MongoDB document via `/api/meta-ads/state`, so
 * every team member sees the same dataset.
 *
 * Two entry points:
 *   /meta-ads  → behind ProtectedRoute, staff session, attribution chip shown
 *   /fbads     → public, no login required, anonymous edits stamped "public"
 */

const fmtRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diff = Date.now() - then;
  if (diff < 0) return 'just now';
  const sec = Math.floor(diff / 1000);
  if (sec < 30) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

const MetaAdsTracker = ({ publicMode = false }) => {
  const auth = useAuth();
  const token = !publicMode ? auth.token : null;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [meta, setMeta] = useState(null);   // { updated_by, updated_at }
  const [tick, setTick] = useState(0);      // for relative-time re-render
  const [iframeHeight, setIframeHeight] = useState(900); // grows to match content

  // Listen for height broadcasts from the inner tool so the iframe resizes to
  // its content. This kills the second scrollbar — the page itself becomes
  // the only scroll context.
  useEffect(() => {
    const onMsg = (e) => {
      const d = e && e.data;
      if (d && d.type === 'meta-tracker:height' && typeof d.height === 'number') {
        // +8px buffer so the bottom shadow / footer never gets clipped.
        setIframeHeight(Math.max(400, Math.ceil(d.height) + 8));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Pass auth (when present) + backend URL via URL hash so the inner tool can
  // call /api/meta-ads/state. Hash never reaches HTTP referer or server logs.
  const iframeSrc = useMemo(() => {
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    params.set('api', backendUrl || '');
    return `/meta-tracker.html?v=7#${params.toString()}`;
  }, [token, backendUrl]);

  // Poll the backend every 30s so the "last updated" chip and any new data
  // from other team members shows up without a hard refresh.
  useEffect(() => {
    let cancelled = false;
    const fetchMeta = async () => {
      try {
        const r = await fetch(`${backendUrl}/api/meta-ads/state`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setMeta(d?._meta || null);
      } catch (e) {
        // silent — chip is non-critical
      }
    };
    fetchMeta();
    const interval = setInterval(fetchMeta, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [backendUrl, token]);

  // Tick every 30s so the "Xm ago" string stays fresh.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const lastUpdatedLabel = useMemo(() => {
    if (!meta?.updated_at) return null;
    // tick is intentionally referenced to refresh memo without adding to deps
    void tick;
    return fmtRelative(meta.updated_at);
  }, [meta, tick]);

  return (
    <div className="min-h-screen bg-page flex flex-col" data-testid="meta-ads-page">
      {!publicMode && <Header />}

      {publicMode && (
        // Lightweight public top bar — keeps the page reachable at /fbads
        // without a login but still gives a one-tap path back to staff sign-in.
        <header className="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md sticky top-0 z-10" data-testid="meta-ads-public-header">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
            <a href="/fbads" className="flex items-center gap-2 group">
              <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-crm-blue to-crm-blue-dark text-white grid place-items-center font-bold text-sm shadow-sm">
                D
              </span>
              <span className="text-sm font-bold tracking-tight text-slate-900 dark:text-white group-hover:text-crm-blue transition-colors">
                Derwent <span className="text-crm-blue">Finance</span>
              </span>
              <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500 ml-3 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700">
                <Globe2 className="w-3 h-3" /> Public
              </span>
            </a>
            <a
              href="/login"
              data-testid="meta-ads-public-signin"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 transition-opacity"
            >
              <LogIn className="w-3.5 h-3.5" />
              Staff sign-in
            </a>
          </div>
        </header>
      )}

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1400px] w-full mx-auto">
        {/* Hero / Header card — dark gradient guaranteed via inline style so
            Tailwind JIT scanning never strips the colors. */}
        <div
          className="relative overflow-hidden rounded-3xl p-5 sm:p-7 mb-5 text-white"
          style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 45%, #0F172A 100%)',
            boxShadow: '0 18px 50px -20px rgba(15,23,42,0.55)',
          }}
          data-testid="meta-ads-hero"
        >
          {/* Soft glow orbs */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -right-20 w-72 h-72 rounded-full blur-3xl"
            style={{ background: 'rgba(37, 99, 235, 0.35)' }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-20 -left-12 w-64 h-64 rounded-full blur-3xl"
            style={{ background: 'rgba(16, 185, 129, 0.22)' }}
          />

          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full backdrop-blur-sm"
                  style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.92)' }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Shared · Live
                </span>
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest"
                  style={{ color: 'rgba(255,255,255,0.75)' }}
                >
                  <Users2 className="w-3 h-3" /> Team data
                </span>
              </div>
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight flex items-center gap-2.5"
                style={{ color: '#FFFFFF' }}
                data-testid="meta-ads-title"
              >
                <Megaphone className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: '#93C5FD' }} />
                Meta Ads <span style={{ color: '#93C5FD' }}>Tracker</span>
                <BetaBadge size="sm" tone="dark" className="ml-1" />
              </h1>
              <p className="text-sm sm:text-[15px] mt-2 max-w-2xl leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                Daily Facebook &amp; Instagram spend, leads and settlements — one source of truth for the whole team. Log it, see what&apos;s working, scale what wins.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {lastUpdatedLabel && (
                <span
                  data-testid="meta-ads-last-updated"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm"
                  style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.96)' }}
                  title={`Last updated ${new Date(meta.updated_at).toLocaleString('en-AU')} by ${meta.updated_by || 'unknown'}`}
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>
                    Last update <strong className="font-bold">{lastUpdatedLabel}</strong>
                    {meta?.updated_by && meta.updated_by !== 'public' && (
                      <> · <span style={{ color: '#93C5FD' }}>{meta.updated_by}</span></>
                    )}
                  </span>
                </span>
              )}
              <a
                href={iframeSrc}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)', color: '#FFFFFF' }}
                data-testid="meta-ads-open-fullscreen"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Open full-screen</span>
                <span className="sm:hidden">Full-screen</span>
              </a>
            </div>
          </div>
        </div>

        {/* Tool frame — auto-grows to the content height it broadcasts so the
            page has a single, smooth scroll. */}
        <div className="rounded-3xl overflow-hidden border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.45)]">
          {iframeSrc ? (
            <iframe
              key={iframeSrc}
              src={iframeSrc}
              title="Derwent Finance — Meta Ads Tracker"
              data-testid="meta-ads-iframe"
              className="w-full block bg-white"
              style={{ height: `${iframeHeight}px`, border: 0, transition: 'height 0.15s ease' }}
              scrolling="no"
              // First-party static file we author — allow same-origin scripts/
              // forms so its vanilla JS can fetch the backend with the JWT in
              // the URL hash.
              sandbox="allow-scripts allow-same-origin allow-downloads allow-forms allow-popups"
            />
          ) : (
            <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
              Loading…
            </div>
          )}
        </div>

        {/* Footnote */}
        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap text-[11px] text-slate-500 dark:text-slate-400">
          <p className="leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-emerald-500" />
            Data lives in a single shared <code className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">meta_ads_state</code> document in MongoDB and is replicated to every session.
            Use the <strong className="text-slate-700 dark:text-slate-200">⭳ Backup</strong> button inside the tool to export a JSON safety net at any time.
          </p>
          {publicMode && (
            <p className="text-slate-400 dark:text-slate-500">
              Viewing the public dashboard · <a href="/login" className="font-semibold text-slate-700 dark:text-slate-200 hover:text-crm-blue underline-offset-4 hover:underline">staff sign-in</a> for full CRM access.
            </p>
          )}
        </div>
      </main>

      {!publicMode && <Footer />}
    </div>
  );
};

export default MetaAdsTracker;
